import { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from '@/features/auth/services/authApi';

interface StrengthResult {
  score: number;
  label: string;
  color: string;
  checks: { label: string; passed: boolean }[];
}

function getPasswordStrength(pw: string): StrengthResult {
  const checks = [
    { label: 'At least 8 characters', passed: pw.length >= 8 },
    { label: 'Lowercase letter', passed: /[a-z]/.test(pw) },
    { label: 'Uppercase letter', passed: /[A-Z]/.test(pw) },
    { label: 'Number', passed: /\d/.test(pw) },
    { label: 'Special character (!@#$...)', passed: /[^A-Za-z0-9]/.test(pw) },
  ];
  const passed = checks.filter((c) => c.passed).length;

  if (passed <= 1) return { score: 0, label: 'Weak', color: '#ef4444', checks };
  if (passed === 2) return { score: 1, label: 'Weak', color: '#ef4444', checks };
  if (passed === 3) return { score: 2, label: 'Fair', color: '#f97316', checks };
  if (passed === 4) return { score: 3, label: 'Good', color: '#3b82f6', checks };
  return { score: 4, label: 'Strong', color: '#22c55e', checks };
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const strength = useMemo(() => getPasswordStrength(password), [password]);
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    passwordRef.current?.focus();
  }, []);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm">
          <h1 className="text-xl font-bold mb-4 text-accent">Invalid Reset Link</h1>
          <hr className="mb-4" />
          <p className="text-sm text-muted mb-4">
            No reset token found. Please request a new reset link.
          </p>
          <Link to="/forgot-password" className="text-sm">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (strength.score < 2) {
      setError('Password is too weak. Please meet at least 3 of the requirements below.');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, password);
      navigate('/login', { state: { passwordReset: true } });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-xl font-bold mb-2 text-accent">Set New Password</h1>
        <p className="text-sm text-muted mb-6">
          Enter your new password below.
        </p>
        <hr className="mb-6" />

        {error && (
          <div className="text-sm text-red-500 mb-4">
            <p>{error}</p>
            {(error.includes('expired') || error.includes('Invalid')) && (
              <p className="mt-2">
                <Link to="/forgot-password" className="underline">Request a new link</Link>
              </p>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-xs font-bold mb-1">New Password</label>
            <input
              id="password"
              ref={passwordRef}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-2 py-1.5 border border-border text-sm font-mono focus:outline-none focus:border-accent"
            />

            {password.length > 0 && (
              <div className="mt-2">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="flex-1 flex gap-1">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="h-1 flex-1 rounded-full"
                        style={{
                          backgroundColor: i <= strength.score - 1
                            ? strength.color
                            : 'rgba(255,255,255,0.1)',
                        }}
                      />
                    ))}
                  </div>
                  <span className="text-xs font-bold" style={{ color: strength.color }}>
                    {strength.label}
                  </span>
                </div>

                <ul className="space-y-0.5">
                  {strength.checks.map((c) => (
                    <li key={c.label} className="text-xs flex items-center gap-1.5">
                      <span style={{ color: c.passed ? '#22c55e' : 'var(--color-muted)' }}>
                        {c.passed ? '\u2713' : '\u2717'}
                      </span>
                      <span className={c.passed ? '' : 'text-muted'}>{c.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="confirm-password" className="block text-xs font-bold mb-1">Confirm Password</label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full px-2 py-1.5 border border-border text-sm font-mono focus:outline-none focus:border-accent"
            />
            {confirmPassword.length > 0 && password !== confirmPassword && (
              <p className="text-xs text-red-500 mt-1">Passwords do not match.</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-terminal disabled:opacity-50"
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <p className="text-sm text-muted mt-6">
          <Link to="/login">Back to Sign In</Link>
        </p>
      </div>
    </div>
  );
}
